if (!customElements.get('sample-pack-selector')) {
  customElements.define(
    'sample-pack-selector',
    class SamplePackSelector extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.maxSamples = Number(this.dataset.maxSamples || 5);
        this.maxIdentical = Number(this.dataset.maxIdentical || 4);
        this.packPrice = this.dataset.packPrice || '$12';
        this.productPurchasable = this.dataset.productPurchasable === 'true';
        this.selections = [];
        this.nextSelectionId = 1;
        this.form = document.getElementById(this.dataset.formId);
        this.submitButton = this.form?.querySelector('[data-sample-pack-submit]');
        this.submitText = this.submitButton?.querySelector('span');
        this.lastGuideTrigger = null;
        this.mobileDisclosure = window.matchMedia('(max-width: 989px)');
        this.handleDisclosureChange = this.syncProductDisclosure.bind(this);

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.form?.addEventListener('submit', this.onFormSubmit.bind(this), true);
        this.mobileDisclosure.addEventListener?.('change', this.handleDisclosureChange);

        this.sortProductsByAvailability();
        this.setFilter('felt');
        this.update();
      }

      disconnectedCallback() {
        this.mobileDisclosure?.removeEventListener?.('change', this.handleDisclosureChange);
      }

      onClick(event) {
        const filter = event.target.closest('[data-sample-filter]');
        if (filter) {
          this.setFilter(filter.dataset.sampleFilter);
          return;
        }

        const toggle = event.target.closest('[data-product-toggle]');
        if (toggle) {
          this.toggleProduct(toggle.closest('[data-sample-product]'));
          return;
        }

        const add = event.target.closest('[data-sample-add]');
        if (add) {
          this.addSelection(add.closest('[data-sample-product]'));
          return;
        }

        const remove = event.target.closest('[data-sample-remove]');
        if (remove) {
          this.removeSelection(Number(remove.dataset.sampleRemove), remove);
          return;
        }

        const emptySlot = event.target.closest('[data-sample-empty-slot]');
        if (emptySlot) {
          const firstProduct = this.querySelector('[data-sample-product]:not([hidden]):not(.is-sold-out)');
          firstProduct?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstProduct?.querySelector('[data-sample-size]:not(:disabled)')?.focus({ preventScroll: true });
          return;
        }

        const guide = event.target.closest('[data-guide-open]');
        if (guide) {
          this.openGuide(guide);
          return;
        }

        const close = event.target.closest('[data-guide-close]');
        if (close) {
          close.closest('dialog')?.close();
          return;
        }

        const dialog = event.target.closest('[data-guide-dialog]');
        if (dialog && event.target === dialog) dialog.close();
      }

      onChange(event) {
        if (!event.target.matches('[data-sample-size]')) return;
        this.setMessage('');
        this.updateAddButtons();
      }

      onFormSubmit(event) {
        if (this.productPurchasable && this.selections.length > 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.productPurchasable) {
          this.setMessage('The Sample Pack is currently sold out.', true);
          return;
        }
        this.setMessage('Choose at least 1 sample before adding the Sample Pack to your cart.', true);
        this.querySelector('[data-sample-product]:not([hidden]) [data-sample-size]:not(:disabled)')?.focus();
      }

      sortProductsByAvailability() {
        const list = this.querySelector('[data-sample-list]');
        if (!list) return;

        Array.from(this.querySelectorAll('[data-sample-product]'))
          .map((row, index) => ({ row, index, soldOut: row.classList.contains('is-sold-out') }))
          .sort((a, b) => Number(a.soldOut) - Number(b.soldOut) || a.index - b.index)
          .forEach(({ row }) => list.append(row));
      }

      setFilter(category) {
        this.activeCategory = category;
        this.querySelectorAll('[data-sample-filter]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.sampleFilter === category));
        });

        let visibleCount = 0;
        this.querySelectorAll('[data-sample-product]').forEach((row) => {
          const categories = (row.dataset.categories || '').split(/\s+/);
          row.hidden = !categories.includes(category);
          if (!row.hidden) visibleCount += 1;
        });

        const label = this.querySelector(`[data-sample-filter="${category}"]`)?.textContent.trim() || category;
        const count = this.querySelector('[data-category-count]');
        if (count) count.textContent = `${visibleCount} ${label} ${visibleCount === 1 ? 'product' : 'products'}`;
        this.syncProductDisclosure();
      }

      syncProductDisclosure() {
        const rows = Array.from(this.querySelectorAll('[data-sample-product]'));
        if (!this.mobileDisclosure?.matches) {
          rows.forEach((row) => this.setProductExpanded(row, true));
          return;
        }

        const visibleRows = rows.filter((row) => !row.hidden);
        const preferredRow = visibleRows.find((row) => !row.classList.contains('is-sold-out')) || visibleRows[0];
        rows.forEach((row) => {
          const shouldOpen = row === preferredRow;
          this.setProductExpanded(row, shouldOpen);
        });
      }

      toggleProduct(row) {
        if (!row || !this.mobileDisclosure?.matches) return;
        const toggle = row.querySelector('[data-product-toggle]');
        const willOpen = toggle?.getAttribute('aria-expanded') !== 'true';
        this.querySelectorAll('[data-sample-product]:not([hidden])').forEach((otherRow) => {
          this.setProductExpanded(otherRow, otherRow === row ? willOpen : false);
        });
      }

      setProductExpanded(row, expanded) {
        const toggle = row.querySelector('[data-product-toggle]');
        const panel = row.querySelector('[data-product-panel]');
        if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
        if (panel) panel.hidden = !expanded;
        row.classList.toggle('is-expanded', expanded);
      }

      addSelection(row) {
        if (!row || this.selections.length >= this.maxSamples) {
          this.setMessage('Your Sample Pack is full. Remove a piece to make a change.', true);
          return;
        }

        const select = row.querySelector('[data-sample-size]');
        const option = select?.selectedOptions?.[0];
        if (!select || select.disabled || !option || option.disabled) {
          this.setMessage('Choose an available size.', true);
          return;
        }

        const variantId = option.value;
        const identicalCount = this.selections.filter((item) => item.variantId === variantId).length;
        if (identicalCount >= this.maxIdentical) {
          this.setMessage('You already have 4 of this product and size. Choose another option.', true);
          return;
        }

        const selection = {
          id: this.nextSelectionId++,
          variantId,
          productId: row.dataset.productId,
          title: row.dataset.productTitle,
          image: row.dataset.productImage,
          size: option.dataset.sizeLabel || option.textContent.trim(),
        };

        this.selections.push(selection);
        this.setMessage(`Added ${selection.title}, ${selection.size}.`);
        this.update();
      }

      removeSelection(id, trigger) {
        const removeButtons = Array.from(this.querySelectorAll('[data-sample-remove]'));
        const focusIndex = Math.max(0, removeButtons.indexOf(trigger));
        const selection = this.selections.find((item) => item.id === id);
        this.selections = this.selections.filter((item) => item.id !== id);
        if (selection) this.setMessage(`Removed ${selection.title}, ${selection.size}.`);
        this.update();
        requestAnimationFrame(() => {
          const remaining = Array.from(this.querySelectorAll('[data-sample-remove]'));
          remaining[Math.min(focusIndex, remaining.length - 1)]?.focus();
        });
      }

      update() {
        this.renderSummary();
        this.updateProperties();
        this.updateProgress();
        this.updateAddButtons();
        this.updateSubmitButton();
      }

      updateProgress() {
        const count = this.selections.length;
        const countLabel = this.querySelector('[data-sample-count]');
        const summaryCount = this.querySelector('[data-summary-count]');
        const progress = this.querySelector('[data-sample-progress]');
        const progressRegion = this.querySelector('[data-sample-progress-region]');

        if (countLabel) countLabel.textContent = `${count} of ${this.maxSamples} selected`;
        if (summaryCount) summaryCount.textContent = `${count} of ${this.maxSamples} selected`;
        if (progress) progress.style.transform = `scaleX(${count / this.maxSamples})`;
        if (progressRegion) {
          progressRegion.setAttribute('aria-valuemax', String(this.maxSamples));
          progressRegion.setAttribute('aria-valuenow', String(count));
          progressRegion.setAttribute('aria-valuetext', `${count} of ${this.maxSamples} selected`);
        }
      }

      updateAddButtons() {
        this.querySelectorAll('[data-sample-product]').forEach((row) => {
          const select = row.querySelector('[data-sample-size]');
          const button = row.querySelector('[data-sample-add]');
          const option = select?.selectedOptions?.[0];
          const soldOut = !select || select.disabled || !option || option.disabled;
          const identicalCount = option
            ? this.selections.filter((item) => item.variantId === option.value).length
            : 0;
          const packFull = this.selections.length >= this.maxSamples;
          const identicalFull = identicalCount >= this.maxIdentical;
          const disabled = soldOut || packFull || identicalFull;
          const rowCount = this.selections.filter((item) => item.productId === row.dataset.productId).length;
          const addedCount = row.querySelector('[data-row-added]');

          if (addedCount) {
            addedCount.hidden = rowCount === 0;
            addedCount.textContent = `${rowCount} selected`;
          }

          if (!button) return;
          button.disabled = disabled;
          if (soldOut) button.textContent = 'Sold out';
          else if (packFull) button.textContent = 'Full';
          else if (identicalFull) button.textContent = 'Max 4';
          else button.textContent = 'Add 1x';

          if (!soldOut) {
            button.setAttribute(
              'aria-label',
              disabled
                ? button.textContent
                : `Add ${row.dataset.productTitle}, ${option.dataset.sizeLabel}, to your Sample Pack`
            );
          }
        });
      }

      updateSubmitButton() {
        if (!this.submitButton || !this.submitText) return;
        const hasSelection = this.selections.length > 0;
        const canSubmit = this.productPurchasable && hasSelection;
        this.submitButton.disabled = !canSubmit;
        this.submitButton.toggleAttribute('aria-disabled', !canSubmit);
        if (!this.productPurchasable) this.submitText.textContent = 'Sample Pack sold out';
        else if (!hasSelection) this.submitText.textContent = 'Choose a sample';
        else this.submitText.textContent = `Add Sample Pack · ${this.packPrice}`;
      }

      updateProperties() {
        const inputs = Array.from(this.querySelectorAll('[data-sample-property]'));
        inputs.forEach((input, index) => {
          const selection = this.selections[index];
          input.disabled = !selection;
          input.value = selection ? `${selection.title} | ${selection.size}` : '';
        });

        const variantIds = this.querySelector('[data-sample-variant-ids]');
        if (variantIds) {
          variantIds.disabled = this.selections.length === 0;
          variantIds.value = this.selections.map((selection) => selection.variantId).join(',');
        }
      }

      renderSummary() {
        const summary = this.querySelector('[data-sample-summary]');
        const empty = this.querySelector('[data-sample-empty]');
        if (!summary || !empty) return;

        summary.replaceChildren();
        empty.hidden = true;

        this.selections.forEach((selection) => {
          const item = document.createElement('div');
          item.className = 'cca-sample-pack__selected-item';

          const thumb = document.createElement('span');
          thumb.className = 'cca-sample-pack__selected-thumb';
          const image = document.createElement('img');
          image.src = selection.image;
          image.alt = '';
          image.loading = 'lazy';
          thumb.append(image);

          const copy = document.createElement('span');
          copy.className = 'cca-sample-pack__selected-copy';
          const title = document.createElement('strong');
          title.textContent = selection.title;
          const size = document.createElement('span');
          size.textContent = selection.size;
          copy.append(title, size);

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'cca-sample-pack__remove';
          remove.dataset.sampleRemove = String(selection.id);
          remove.setAttribute('aria-label', `Remove ${selection.title}, ${selection.size}`);
          remove.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 6 8 8M14 6l-8 8"/></svg>';

          item.append(thumb, copy, remove);
          summary.append(item);
        });

        for (let slot = this.selections.length; slot < this.maxSamples; slot += 1) {
          const placeholder = document.createElement('button');
          placeholder.type = 'button';
          placeholder.className = 'cca-sample-pack__selected-placeholder';
          placeholder.dataset.sampleEmptySlot = String(slot + 1);
          placeholder.setAttribute('aria-label', `Choose sample ${slot + 1}`);
          placeholder.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 4v12M4 10h12"/></svg>';
          summary.append(placeholder);
        }
      }

      openGuide(trigger) {
        const dialog = this.querySelector('[data-guide-dialog]');
        const image = this.querySelector('[data-guide-image]');
        const heading = this.querySelector('[data-guide-heading]');
        if (!dialog || !image || !heading || !trigger.dataset.guideSrc) return;

        this.lastGuideTrigger = trigger;
        heading.textContent = trigger.dataset.guideTitle || 'How to measure';
        image.src = trigger.dataset.guideSrc;
        image.alt = trigger.dataset.guideAlt || '';
        dialog.addEventListener('close', () => this.lastGuideTrigger?.focus(), { once: true });
        dialog.showModal();
      }

      setMessage(message, visible = false) {
        const region = this.querySelector('[data-sample-message]');
        if (!region) return;
        region.textContent = message;
        region.toggleAttribute('data-visible', visible && Boolean(message));
      }
    }
  );
}
